import * as React from "react";
import { AlertTriangle, CloudOff, Loader2, RefreshCcw } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOutbox } from "@/hooks/useOutbox";
import { clientLog } from "@/lib/client-logger";
import { type OutboxFieldNote, type OutboxPunch } from "@/lib/db";
import { retryFieldNote, retryPunch } from "@/lib/outbox";
import { requestSync } from "@/lib/syncScheduler";

function punchTimeLabel(punch: OutboxPunch): string {
  const raw = punch.fields.current_time;
  const parsed = raw ? new Date(raw) : new Date(punch.createdAt);
  return Number.isNaN(parsed.getTime()) ? new Date(punch.createdAt).toLocaleString() : parsed.toLocaleString();
}

function statusLabel(status: OutboxPunch["status"]): string {
  switch (status) {
    case "pending":
      return "Waiting to sync";
    case "syncing":
      return "Syncing…";
    case "failed":
      return "Failed";
    case "auth_required":
      return "Sign-in required";
    default:
      return status;
  }
}

function SelfieThumbnail({ blob }: { blob: Blob | null }) {
  const url = React.useMemo(() => {
    try {
      return blob instanceof Blob ? URL.createObjectURL(blob) : null;
    } catch (error) {
      clientLog("warn", "sync_status:selfie_thumbnail:create_url_failed", {
        hasBlob: !!blob,
        blobType: blob?.type,
        blobBytes: blob?.size,
        errorName: (error as any)?.name,
        errorMessage: String((error as any)?.message || (error as any)?.name || ""),
      });
      return null;
    }
  }, [blob]);
  React.useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);
  if (!url) return <div className="size-12 rounded-md bg-muted" />;
  return (
    <img
      src={url}
      alt="Punch selfie"
      className="size-12 rounded-md object-cover"
      onError={() => clientLog("warn", "sync_status:selfie_thumbnail:image_load_failed", {
        blobType: blob?.type,
        blobBytes: blob?.size,
      })}
    />
  );
}

function QueuedPunchRow({ punch }: { punch: OutboxPunch }) {
  const isFailed = punch.status === "failed";
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <SelfieThumbnail blob={punch.selfieBlob} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{punch.action === "clock_in" ? "Clock In" : "Clock Out"}</span>
          <Badge variant={isFailed ? "destructive" : "secondary"}>{statusLabel(punch.status)}</Badge>
        </div>
        <div className="text-xs text-muted-foreground">Recorded {punchTimeLabel(punch)}</div>
        {punch.lastError && (
          <div className="truncate text-xs text-destructive" title={punch.lastError}>
            {punch.lastError}
          </div>
        )}
      </div>
      {isFailed && (
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant="outline" onClick={() => { void retryPunch(punch.id!); requestSync({ force: true }); }}>
            <RefreshCcw className="size-3.5" />
            Retry
          </Button>
        </div>
      )}
      {/* Discard button is intentionally not available here for unsynced/failed items.
          Per policy, discard should only target synced contents (use the storage "Clear synced" button or prune for cleanup). */}
    </div>
  );
}

export function SyncStatusBadge({ userId }: { userId: number | undefined }) {
  const { queued, fieldNoteQueued, pendingCount, syncing, authRequired, failed, fieldNoteFailed, syncNow } = useOutbox(userId);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (pendingCount === 0 && open) {
      setOpen(false);
    }
  }, [pendingCount, open]);

  if (pendingCount === 0) return null;

  const attention = authRequired || failed.length > 0 || fieldNoteFailed.length > 0;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="focus:outline-none focus:ring-2 focus:ring-ring rounded-full"
        aria-label={`${pendingCount} items waiting to sync`}
      >
        <Badge variant={attention ? "destructive" : "secondary"} className="gap-1">
          {syncing ? <Loader2 className="size-3 animate-spin" /> : <CloudOff className="size-3" />}
          {pendingCount} pending
        </Badge>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Items waiting to sync</DialogTitle>
            <DialogDescription>
              Punches and field notes are stored safely on this device. They upload automatically when you are back online.
            </DialogDescription>
          </DialogHeader>
          {authRequired && (
            <Alert className="border-destructive/20 bg-destructive/10 text-destructive">
              <AlertTriangle className="size-4" />
              <div className="mb-1 font-medium leading-none">Sign-in required</div>
              <div className="text-sm">Your session expired. Sign in again and queued items will sync automatically.</div>
            </Alert>
          )}
          <div className="grid gap-2">
            {queued.map((punch) => (
              <QueuedPunchRow key={punch.uuid} punch={punch} />
            ))}
            {fieldNoteQueued.map((note) => (
              <FieldNoteRow key={note.uuid} note={note} />
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => syncNow()} disabled={syncing}>
              {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCcw className="size-4" />}
              Sync now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Lightweight row for field notes in the sync panel (no photo; shows summary of the note).
function FieldNoteRow({ note }: { note: OutboxFieldNote }) {
  const isFailed = note.status === "failed";
  const summary = (note.fields.additional_note as string) || "Field note";
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Field Note</span>
          <Badge variant={isFailed ? "destructive" : "secondary"}>{statusLabel(note.status)}</Badge>
        </div>
        <div className="truncate text-xs text-muted-foreground">{summary}</div>
        {note.lastError && (
          <div className="truncate text-xs text-destructive" title={note.lastError}>
            {note.lastError}
          </div>
        )}
      </div>
      {isFailed && (
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant="outline" onClick={() => { void retryFieldNote(note.id!); requestSync({ force: true }); }}>
            <RefreshCcw className="size-3.5" />
            Retry
          </Button>
        </div>
      )}
      {/* Discard intentionally omitted for unsynced items; only for synced contents (see storage management). */}
    </div>
  );
}
